import React, { useMemo } from 'react';
import { Stack, Text, Box } from '@chakra-ui/react';
import { NestedHome, NearbyResult } from '@habity/types';
import { Nearby } from './Nearby';
import { NearbyEditor } from './NearbyEditor';
import { useNearbyEditor } from '~/hooks/useNearbyEditor';
import { useHomes } from '~/hooks/useHomes';
import { useUser } from '~/hooks/useUser';
import { useAgency } from '~/hooks/useAgency';
import { getMaxDisplayDistance } from '~/lib/homes/profile/nearbyConstants';
import { revalidateHome } from '~/app/actions/revalidate';
import { useRouter } from 'next/navigation';
import { isBrokerOrAdmin } from '~/lib/roles';

type NearbyWithEditorProps = {
  home: NestedHome;
};

export const NearbyWithEditor: React.FC<NearbyWithEditorProps> = ({ home }) => {
  const { updateHome } = useHomes();
  const { user } = useUser();
  const { agency } = useAgency();
  const router = useRouter();

  // Check if user has permission to edit
  const isUserAllowedToEdit = React.useMemo(() => {
    if (!user || !agency) return false;
    return isBrokerOrAdmin(user);
  }, [user, agency]);

  // Calculate max display distance and filter items
  const { filteredItems, maxDistance } = useMemo(() => {
    const items = home.nearby_json || [];

    if (items.length === 0) {
      return { filteredItems: [], maxDistance: Infinity };
    }

    // Get minimum distance to determine filtering
    const distances = items
      .map((item) => item.distance)
      .filter((d) => typeof d === 'number');
    const minDistance =
      distances.length > 0 ? Math.min(...distances) : Infinity;
    const maxDist = getMaxDisplayDistance(minDistance);

    // Filter items based on max distance
    const filtered =
      maxDist === Infinity
        ? items
        : items.filter((item) => item.distance < maxDist);

    return { filteredItems: filtered, maxDistance: maxDist };
  }, [home.nearby_json]);

  const handleSaveNearby = async (items: NearbyResult[]) => {
    if (!home?.id) {
      throw new Error('Home ID is required');
    }

    // Update the home with new nearby_json data
    await updateHome(home.id, {
      nearby_json: JSON.stringify(items),
    });

    // Revalidate the home page cache to show updated data
    await revalidateHome(home);

    // Refresh the current route to get the latest data
    router.refresh();
  };

  const {
    isEditing,
    isSaving,
    editItems,
    handleEdit,
    handleCancel,
    handleSave,
    handleAddItem,
    handleRemoveItem,
    handleUpdateItem,
  } = useNearbyEditor(filteredItems, handleSaveNearby);

  return (
    <Stack spacing={4}>
      {/* Display nearby items */}
      {!isEditing && <Nearby home={home} />}

      {/* Editor */}
      <NearbyEditor
        isEditing={isEditing}
        isSaving={isSaving}
        editItems={editItems}
        canEdit={isUserAllowedToEdit}
        maxDistance={maxDistance}
        onEdit={handleEdit}
        onCancel={handleCancel}
        onSave={handleSave}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
        onUpdateItem={handleUpdateItem}
      />

      {/* Distance filter info */}
      {isEditing && maxDistance !== Infinity && (
        <Box p={3} bg="blue.50" borderRadius="md" fontSize="sm">
          <Text color="blue.800">
            ℹ️ Visar endast platser inom {maxDistance}m eftersom närmaste plats
            är närmare än {maxDistance}m. Platser längre bort visas inte i
            listan.
          </Text>
        </Box>
      )}
    </Stack>
  );
};
